/**
 * Design Memory — similarity report.
 *
 * Compares a candidate fingerprint against a set of stored fingerprints and
 * summarizes: the nearest prior design, the per-dimension maxima (which axes
 * are most repeated), and whether the candidate is a duplicate. Deterministic.
 */

import { SIMILARITY_DIMENSIONS, WEIGHTS, DEFAULT_THRESHOLDS } from './memoryVocabulary';
import { computeSimilarity } from './similarity';

/**
 * @param {Object} candidate  fingerprint under evaluation.
 * @param {Object[]} stored    prior fingerprints (already scoped).
 * @param {Object} [opts]
 * @param {number} [opts.threshold]  duplicate threshold (default 0.7).
 * @param {Object} [opts.weights]
 * @param {Function} [opts.similarity]  strategy (default categorical) — the embedding seam.
 * @returns {Object} similarity report.
 */
export const computeSimilarityReport = (candidate, stored = [], opts = {}) => {
  const {
    threshold = DEFAULT_THRESHOLDS.duplicate,
    weights = WEIGHTS,
    similarity = computeSimilarity,
  } = opts;

  const perDimensionMax = {};
  SIMILARITY_DIMENSIONS.forEach((d) => { perDimensionMax[d] = 0; });

  let nearest = null;
  let maxSimilarity = 0;

  stored.forEach((fp) => {
    if (!fp || fp.id === candidate.id) return; // never compare against self
    const result = similarity(candidate, fp, weights);
    if (result.overall > maxSimilarity) {
      maxSimilarity = result.overall;
      nearest = { id: fp.id, overall: result.overall, dimensions: result.dimensions, fingerprint: fp };
    }
    SIMILARITY_DIMENSIONS.forEach((d) => {
      if (result.dimensions[d] > perDimensionMax[d]) perDimensionMax[d] = result.dimensions[d];
    });
  });

  return {
    maxSimilarity,
    duplicate: maxSimilarity > threshold,
    threshold,
    nearest,
    perDimensionMax,
    comparisons: stored.filter((fp) => fp && fp.id !== candidate.id).length,
  };
};
